import { api } from './client'

export interface LdapPerson {
  username: string
  display_name: string
  email: string
  spn: string
  is_locked: boolean
  groups: string[]
  credential_status?: LdapCredentialStatus
  unix_attrs?: LdapUnixAttrs | null
}

export interface LdapGroup {
  name: string
  display_name: string
  description: string
  spn: string
  members: string[]
  member_count: number
  is_system: boolean
}

export interface LdapCredentialStatus {
  // `null` = desconhecido. O endpoint de status do Kanidm NÃO expõe a credencial
  // unix, e numa falha de leitura nada pode ser afirmado — antes vinha `false`
  // sempre, e a tela dizia "Sem senha primária" para 100% da base.
  has_primary: boolean | null
  has_unix: boolean | null
  has_passkey: boolean
  credentials: { id: string; type: string; label: string }[]
}

export interface LdapUnixAttrs {
  gidnumber: number | null
  uidnumber: number | null
  loginshell: string | null
}

export interface LdapOAuth2ScopeMap {
  group: string
  group_spn: string
  scopes: string[]
}

export interface LdapOAuth2Client {
  name: string
  displayname: string
  landing_url: string
  redirects: string[]
  scopemaps: LdapOAuth2ScopeMap[]
  pkce_obrigatorio: boolean
  protegido: boolean
  publico: boolean
  localhost_redirect: boolean
}

export interface LdapOAuth2Create {
  name: string
  displayname: string
  landing_url: string
  redirects: string[]
}

export interface LdapOAuth2CreatePublic extends LdapOAuth2Create { localhost_redirect: boolean }

// Corpo alternativo de uma mutação: o Kanidm aceitou a escrita, mas a releitura
// (feita para devolver o RS atualizado) falhou. A trilha de auditoria já registrou
// sucesso (`ldap_oauth2_routes.py`) — a UI não pode tratar isto como cliente nem
// como erro; tem de avisar e reinvalidar a lista.
export interface LdapOAuth2ReleituraFalhou {
  name: string
  releitura: 'falhou'
}

export function isReleituraFalhou(
  x: LdapOAuth2Client | LdapOAuth2ReleituraFalhou,
): x is LdapOAuth2ReleituraFalhou {
  return (x as LdapOAuth2ReleituraFalhou).releitura === 'falhou'
}

export const ldapApi = {
  // Persons
  listPersons: () =>
    api.get<{ items: LdapPerson[] }>('/v1/ldap/persons').then((r) => r.data),

  getPerson: (username: string) =>
    api.get<LdapPerson>(`/v1/ldap/persons/${username}`).then((r) => r.data),

  createPerson: (data: { username: string; display_name: string; email: string }) =>
    api
      .post<{ person: LdapPerson; reset_token: string }>('/v1/ldap/persons', data)
      .then((r) => r.data),

  updatePerson: (username: string, data: { display_name?: string; email?: string }) =>
    api.patch(`/v1/ldap/persons/${username}`, data).then((r) => r.data),

  deletePerson: (username: string) =>
    api.delete(`/v1/ldap/persons/${username}`).then((r) => r.data),

  lockPerson: (username: string) =>
    api.post(`/v1/ldap/persons/${username}/lock`, {}).then((r) => r.data),

  unlockPerson: (username: string) =>
    api.post(`/v1/ldap/persons/${username}/unlock`, {}).then((r) => r.data),

  getCredentials: (username: string) =>
    api
      .get<LdapCredentialStatus>(`/v1/ldap/persons/${username}/credentials`)
      .then((r) => r.data),

  deleteCredential: (username: string, credId: string) =>
    api.delete(`/v1/ldap/persons/${username}/credentials/${credId}`).then((r) => r.data),

  resetPassword: (username: string) =>
    api
      .post<{ reset_token: string }>(`/v1/ldap/persons/${username}/reset-password`, {})
      .then((r) => r.data),

  setUnixCredential: (username: string, password: string) =>
    api
      .post(`/v1/ldap/persons/${username}/unix-credential`, { password })
      .then((r) => r.data),

  getUnixAttrs: (username: string) =>
    api.get<LdapUnixAttrs>(`/v1/ldap/persons/${username}/unix`).then((r) => r.data),

  setUnixAttrs: (username: string, data: { shell?: string; gidnumber?: number }) =>
    api.patch(`/v1/ldap/persons/${username}/unix`, data).then((r) => r.data),

  listSshKeys: (username: string) =>
    api
      .get<{ items: { tag: string; key: string }[] }>(`/v1/ldap/persons/${username}/ssh-keys`)
      .then((r) => r.data),

  addSshKey: (username: string, tag: string, key: string) =>
    api.post(`/v1/ldap/persons/${username}/ssh-keys`, { tag, key }).then((r) => r.data),

  removeSshKey: (username: string, tag: string) =>
    api
      .delete(`/v1/ldap/persons/${username}/ssh-keys/${encodeURIComponent(tag)}`)
      .then((r) => r.data),

  // Groups
  listGroups: (includeSystem = false) =>
    api
      .get<{ items: LdapGroup[] }>(`/v1/ldap/groups?include_system=${includeSystem}`)
      .then((r) => r.data),

  getGroup: (name: string) =>
    api.get<LdapGroup>(`/v1/ldap/groups/${name}`).then((r) => r.data),

  createGroup: (data: { name: string; description?: string }) =>
    api.post<LdapGroup>('/v1/ldap/groups', data).then((r) => r.data),

  deleteGroup: (name: string) =>
    api.delete(`/v1/ldap/groups/${name}`).then((r) => r.data),

  addMembers: (group: string, usernames: string[]) =>
    api.post(`/v1/ldap/groups/${group}/members`, { usernames }).then((r) => r.data),

  removeMember: (group: string, username: string) =>
    api.delete(`/v1/ldap/groups/${group}/members/${username}`).then((r) => r.data),

  // Clientes OAuth2 (RS do Kanidm)
  listOAuth2: () =>
    api.get<{ items: LdapOAuth2Client[]; count: number }>('/v1/ldap/oauth2').then((r) => r.data),

  getOAuth2: (name: string) =>
    api.get<LdapOAuth2Client>(`/v1/ldap/oauth2/${name}`).then((r) => r.data),

  createOAuth2: (data: LdapOAuth2Create) =>
    api
      .post<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>('/v1/ldap/oauth2', data)
      .then((r) => r.data),

  // Cliente público (PKCE, sem segredo). `_public` não casa a regex de nome de RS.
  createOAuth2Public: (data: LdapOAuth2CreatePublic) =>
    api
      .post<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>('/v1/ldap/oauth2/_public', data)
      .then((r) => r.data),

  setOAuth2LocalhostRedirect: (name: string, enabled: boolean) =>
    api
      .put<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>(
        `/v1/ldap/oauth2/${name}/localhost-redirect`, { enabled },
      )
      .then((r) => r.data),

  addOAuth2Redirect: (name: string, url: string) =>
    api
      .post<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>(`/v1/ldap/oauth2/${name}/redirects`, { url })
      .then((r) => r.data),

  // Sem `data`, o axios manda DELETE sem corpo. Isso nem chega ao Kanidm: a rota do
  // ConsoleAI exige a URL no corpo e responde 422 antes de chamar o client (é o
  // DELETE `[]` que apagaria TODOS no Kanidm, não o caminho daqui).
  removeOAuth2Redirect: (name: string, url: string) =>
    api
      .delete<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>(`/v1/ldap/oauth2/${name}/redirects`, {
        data: { url },
      })
      .then((r) => r.data),

  setOAuth2ScopeMap: (name: string, group: string, scopes: string[]) =>
    api
      .put<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>(
        `/v1/ldap/oauth2/${name}/scopemap/${group}`, { scopes },
      )
      .then((r) => r.data),

  removeOAuth2ScopeMap: (name: string, group: string) =>
    api
      .delete<LdapOAuth2Client | LdapOAuth2ReleituraFalhou>(
        `/v1/ldap/oauth2/${name}/scopemap/${group}`,
      )
      .then((r) => r.data),

  revealOAuth2Secret: (name: string) =>
    api.post<{ secret: string }>(`/v1/ldap/oauth2/${name}/secret/reveal`, {}).then((r) => r.data.secret),

  deleteOAuth2: (name: string) =>
    api.delete(`/v1/ldap/oauth2/${name}`).then((r) => r.data),
}
